// src/components/InviteEmployees.js
import React, { useState } from "react";
import { Table, Button, Card, Modal, Form, Input, message, Typography, Tag } from "antd";
import { useInvites } from  "PlatForms/OrgLMS/Hooks/ManagerHooks/useInvites";
import { useNavigate } from "react-router-dom";

const { Text, Title } = Typography;

const InviteEmployees = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [responseModal, setResponseModal] = useState({
    visible: false,
    type: null,
    data: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { invites, loading, handleResend, handleSendInvite, pagination, handlePageChange } = useInvites();

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleResponseModalClose = () => {
    setResponseModal({ visible: false, type: null, data: null });
  };

  const renderStatus = (status) => {
    switch (status) {
      case 1: // Pending
        return <Tag color="warning">Pending</Tag>;
      case 2: // Approved
        return <Tag color="green">Approved</Tag>;
      case 3: // Canceled
        return <Tag color="red">Canceled</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      const result = await handleSendInvite(values);

      if (result.success) {
        setResponseModal({
          visible: true,
          type: "success",
          data: result.data,
        });
        setIsModalVisible(false);
        form.resetFields();
      } else {
        const { type, payload } = result.error;
        if (type === "AlreadyInvite") {
          setResponseModal({
            visible: true,
            type: "alreadyInvited",
            data: payload,
          });
        } else if (type === "AlreadyUser") {
          setResponseModal({
            visible: true,
            type: "alreadyExists",
            data: payload,
          });
        } else {
          message.error(result.error.message || "Failed to send invitation");
        }
      }
    } catch (error) {
      console.error("Validation Failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = [
    {
      title: "Full Name",
      render: (text, record) => `${record.firstName} ${record.lastName}`,
      key: "fullName",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Invited On",
      dataIndex: "inviteSentAt",
      key: "inviteSentAt",
      render: (text) => new Date(text).toLocaleDateString(),
    },
    {
      title: "Status",
      key: "status",
      render: (text, record) => renderStatus(record.status),
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => {
        const inviteSentAt = new Date(record.inviteSentAt);
        const now = new Date();
        const diffInMs = now - inviteSentAt;
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        const canResend = diffInMinutes >= 60;
        const remainingMinutes = 60 - diffInMinutes;

        return (
          <div>
            <Button
              type="primary"
              disabled={!canResend || record.status !== 1}
              onClick={() => handleResend(record.id)}
            >
              Resend
            </Button>
            {record.status === 1 && !canResend && (
              <div style={{ fontSize: 12, color: "#999", marginTop: 4 }}>
                You can resend after {remainingMinutes} minute{remainingMinutes > 1 ? "s" : ""}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const renderResponseModalContent = () => {
    switch (responseModal.type) {
      case "success":
        return (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Title level={3} style={{ color: "#52c41a", marginBottom: 16 }}>
              Success!
            </Title>
            <Text style={{ fontSize: 16, display: "block", marginBottom: 24 }}>
              Invitation sent successfully!
            </Text>
          </div>
        );
      case "alreadyInvited":
        return (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Title level={3} style={{ color: "#faad14", marginBottom: 16 }}>
              Already Invited!
            </Title>
            <Text style={{ fontSize: 16, display: "block", marginBottom: 8 }}>
              {responseModal.data.firstName} {responseModal.data.lastName}
            </Text>
            <Text style={{ fontSize: 16, display: "block", marginBottom: 24 }}>
              ({responseModal.data.email})<br />
              <span style={{ color: "#faad14" }}>Was Already Invited.</span>
            </Text>
            <Text type="secondary" style={{ display: "block" }}>
              Invite sent on: {new Date(responseModal.data.inviteSentAt).toLocaleString()}
            </Text>
          </div>
        );
      case "alreadyExists":
        return (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Title level={3} style={{ color: "#ff4d4f", marginBottom: 16 }}>
              Already Registered!
            </Title>
            <Text style={{ fontSize: 16, display: "block", marginBottom: 8 }}>
              {responseModal.data.firstName} {responseModal.data.lastName}
            </Text>
            <Text style={{ fontSize: 16, display: "block", marginBottom: 24 }}>
              ({responseModal.data.email}) <br />
              <span style={{ color: "red" }}> Is Already Registered.</span>
            </Text>
            <Text type="secondary" style={{ display: "block" }}>
              Account created on: {new Date(responseModal.data.createdOn).toLocaleString()}
            </Text>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Card
      title="Invited Employees"
      loading={loading}
      className="card-professional"
      extra={
        <Button type="default" onClick={() => navigate("/new-employees")}>
          Back
        </Button>
      }
    >
      <Button type="primary" onClick={showModal}>
        Invite Employees
      </Button>
      <br />
      <br />
      <Table
        columns={columns}
        dataSource={invites}
        rowKey="id"
        scroll={{ x: "max-content" }}
        pagination={{
          current: pagination.pageNumber,
          pageSize: pagination.pageSize,
          total: pagination.totalCount,
          onChange: handlePageChange,
          showSizeChanger: false,
        }}
      />

      {/* Invitation Modal */}
      <Modal
        title="Invite New Employee"
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={handleCancel}
        okText="Send Invitation"
        cancelText="Cancel"
        confirmLoading={isSubmitting}
        centered
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="firstName"
            label="First Name"
            rules={[{ required: true, message: "Please input the first name!" }]}
          >
            <Input placeholder="Enter first name" />
          </Form.Item>
          <Form.Item
            name="lastName"
            label="Last Name"
            rules={[{ required: true, message: "Please input the last name!" }]}
          >
            <Input placeholder="Enter last name" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please input the email!" },
              { type: "email", message: "Please enter a valid email!" },
            ]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Response Modal */}
      <Modal
        open={responseModal.visible}
        onCancel={handleResponseModalClose}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={handleResponseModalClose}
            style={{
              backgroundColor:
                responseModal.type === "success"
                  ? "#52c41a"
                  : responseModal.type === "alreadyInvited"
                    ? "#faad14"
                    : "#ff4d4f",
              borderColor:
                responseModal.type === "success"
                  ? "#52c41a"
                  : responseModal.type === "alreadyInvited"
                    ? "#faad14"
                    : "#ff4d4f",
            }}
          >
            OK
          </Button>,
        ]}
        centered
        width={400}
      >
        {renderResponseModalContent()}
      </Modal>
    </Card>
  );
};

export default InviteEmployees;
